import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";
import {
  Users,
  Search,
  Layers,
  Crown,
  ArrowUpRight,
  Sparkles,
  Zap,
  Lock,
} from "lucide-react";
import FAQ from "@/components/FAQ";
import {
  GetStarted,
  BrowseByCategory,
  QuickLinks,
  Footer as DashFooter,
} from "@/components/DashboardExtras";

function Stat({ label, value, sub, testid, icon: Icon, accent = "#9D4EDD" }) {
  return (
    <div className="gc-stat-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="gc-label">{label}</div>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="font-heading text-4xl md:text-5xl font-black mt-3 leading-none">
        {value}
      </div>
      {sub && (
        <div className="text-xs text-[#A1A1AA] mt-3 font-mono">{sub}</div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/stats/overview").then((r) => setData(r.data)).catch(() => {});
    api
      .get("/announcements")
      .then((r) => setAnnouncements(r.data.items || []))
      .catch(() => {});
  }, []);

  const chartData =
    data?.chart?.labels?.map((l, i) => ({
      day: l.slice(5),
      all: data.chart.all[i] || 0,
      mine: data.chart.mine[i] || 0,
    })) || [];

  const totalRecords = "8M+";
  const usedToday = data?.queries_today ?? 0;
  const limit = data?.daily_limit ?? 5;

  const goQuery = () => {
    navigate("/checker" + (q ? `?id=${encodeURIComponent(q)}` : ""));
  };

  return (
    <div className="max-w-none w-full space-y-4">
      {/* HERO */}
      <section className="gc-hero" data-testid="dashboard-hero">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest text-[#9D4EDD] border border-[#9D4EDD]/50 px-2.5 py-1 bg-[#9D4EDD]/10 rounded-full">
          <Sparkles size={10} />
          HOŞ GELDİN, {user?.username?.toUpperCase()}
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mt-4 leading-[0.95]">
          {totalRecords}{" "}
          <span className="text-[#9D4EDD]">kayıt.</span>{" "}
          <br className="hidden md:block" />
          Tek sorgu.
        </h1>
        <p className="mt-3 max-w-2xl text-[13px] text-[#A1A1AA]">
          Sızdırılmış veri kayıtları arasında Discord ID ile derin tarama yapın.
          E-posta, IP, sunucu üyelikleri ve bağlantılar — tek bir terminalde.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            goQuery();
          }}
          className="mt-5 flex flex-col sm:flex-row gap-2 max-w-2xl"
          data-testid="hero-quicksearch"
        >
          <div className="flex-1 relative">
            <Search
              size={14}
              className="absolute top-1/2 -translate-y-1/2 left-3.5 text-[#A1A1AA]"
            />
            <input
              data-testid="hero-quicksearch-input"
              className="gc-input pl-10"
              placeholder="Discord ID gir — anında tara"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="gc-btn-primary flex items-center gap-1.5 justify-center"
            data-testid="hero-quicksearch-submit"
          >
            SORGULA <ArrowUpRight size={12} />
          </button>
        </form>
      </section>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-stats">
        <Stat
          testid="stat-total-users"
          icon={Users}
          label="TOPLAM KULLANICI"
          value={data ? data.total_users : "…"}
          sub="Kayıtlı hesaplar"
        />
        <Stat
          testid="stat-total-queries"
          icon={Search}
          label="TOPLAM SORGU"
          value={data ? data.total_queries : "…"}
          sub="Tüm zamanlar"
          accent="#EAB308"
        />
        <Stat
          testid="stat-total-records"
          icon={Layers}
          label="VERİ KAYDI"
          value={totalRecords}
          sub="Sorgulanabilir"
          accent="#00FF66"
        />
        <Stat
          testid="stat-membership"
          icon={Crown}
          label="ÜYELİK"
          value={data ? data.membership_name : "…"}
          sub={
            data
              ? `${usedToday} / ${limit === -1 ? "∞" : limit} bugün`
              : ""
          }
          accent="#EAB308"
        />
      </div>

      {/* Chart + announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 gc-card p-4" data-testid="chart-queries">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="gc-label">SON 14 GÜN</div>
              <div className="font-heading text-base font-bold flex items-center gap-2 mt-1">
                <Zap size={13} className="text-[#9D4EDD]" />
                Sorgu Trafiği
              </div>
            </div>
            <div className="flex gap-1.5">
              <span className="gc-badge gc-badge-red">Tümü</span>
              <span className="gc-badge gc-badge-gold">Sizin</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gAll" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#9D4EDD" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#9D4EDD" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gMine" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EAB308" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#EAB308" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#141414" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={10} />
                <Tooltip
                  contentStyle={{
                    background: "#050505",
                    border: "1px solid #222",
                    fontFamily: "JetBrains Mono",
                    fontSize: 12,
                    color: "#EDEDED",
                    borderRadius: 0,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="all"
                  stroke="#9D4EDD"
                  strokeWidth={2}
                  fill="url(#gAll)"
                />
                <Area
                  type="monotone"
                  dataKey="mine"
                  stroke="#EAB308"
                  strokeWidth={2}
                  fill="url(#gMine)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4">
            <div className="gc-label mb-2">GÜNLÜK DAĞILIM</div>
            <div style={{ width: "100%", height: 100 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#141414" strokeDasharray="3 3" />
                  <XAxis dataKey="day" stroke="#666" fontSize={10} />
                  <YAxis stroke="#666" fontSize={10} />
                  <Tooltip
                    cursor={{ fill: "rgba(157,78,221,0.06)" }}
                    contentStyle={{
                      background: "#050505",
                      border: "1px solid #222",
                      fontFamily: "JetBrains Mono",
                      fontSize: 12,
                      color: "#EDEDED",
                      borderRadius: 0,
                    }}
                  />
                  <Bar dataKey="all" fill="#9D4EDD" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <aside
          className="gc-card p-4 border-l-2 border-l-[#EAB308]"
          data-testid="announcements-panel"
        >
          <div className="gc-label mb-1">DUYURULAR</div>
          <div className="font-heading text-base font-bold mb-3">Sistem Notları</div>
          <div className="space-y-3 max-h-[440px] overflow-y-auto pr-2">
            {announcements.length === 0 && (
              <div className="text-xs text-[#A1A1AA] font-mono">
                Henüz duyuru yok.
              </div>
            )}
            {announcements.map((a) => (
              <div
                key={a.id}
                className="pb-3 border-b border-[#141414] last:border-b-0"
                data-testid="announcement-item"
              >
                <div className="flex items-center gap-2 mb-1">
                  {a.pinned && <span className="gc-badge gc-badge-red">SABİT</span>}
                  <div className="text-[10px] text-[#A1A1AA] font-mono">
                    {a.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </div>
                <h4 className="font-heading font-bold text-white text-sm">{a.title}</h4>
                <p className="text-xs text-[#A1A1AA] whitespace-pre-wrap mt-1">
                  {a.body}
                </p>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Upgrade banner (only for non-gold) */}
      {data && data.membership_tier !== "gold" && (
        <div
          className="gc-card p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 border-[#9D4EDD]/40"
          data-testid="dashboard-upgrade"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 flex items-center justify-center border border-[#9D4EDD]/50 bg-[#9D4EDD]/10 text-[#9D4EDD] shrink-0 rounded-lg">
              <Lock size={14} />
            </div>
            <div>
              <div className="font-heading text-base font-bold">
                Sınırsız istihbarat için yükselt
              </div>
              <div className="text-xs text-[#A1A1AA] mt-1">
                Gold planı sınırsız sorgu ve öncelikli desteği açar. Ödeme sadece BTC ile.
              </div>
            </div>
          </div>
          <button
            className="gc-btn-primary"
            data-testid="upgrade-btn"
            onClick={() => navigate("/magaza")}
          >
            PLANLARI GÖR
          </button>
        </div>
      )}

      {/* Get Started */}
      <GetStarted user={user} data={data} />

      {/* Browse by Category */}
      <BrowseByCategory />

      {/* FAQ + Quick Links (2-column) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <FAQ />
        </div>
        <QuickLinks />
      </div>

      {/* Footer */}
      <DashFooter />
    </div>
  );
}
